import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  IAutomaticPointPreset,
  AutomaticPointPreset,
  CreateAutomaticPointPresetDto,
  UpdateAutomaticPointPresetDto
} from '../_models/AutomaticPointPreset';
import { PlatesSettings } from '../_models/PlatesSettings';
import { StiffenersSettings } from '../_models/StiffenersSettings';
import { FacePlatesSettings } from '../_models/FacePlatesSettings';
import { BracketSettings } from '../_models/BracketSettings';
import { PointsArrangements } from '../_models/PointsArrangements';
import { v4 as uuid } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class AutomaticPointPresetService {
  private readonly apiUrl = `${environment.API_INSPEC}AutomaticPointPreset`;

  // Feature flag to switch between mock and real API
  private readonly useMockData = false;

  // Mock data store
  private mockPresets: IAutomaticPointPreset[] = [];

  // Observable for presets list
  private presetsSubject = new BehaviorSubject<IAutomaticPointPreset[]>([]);
  public presets$ = this.presetsSubject.asObservable();

  constructor(private http: HttpClient) {}

  // CRUD Operations

  getByAssetId(assetId: string): Observable<IAutomaticPointPreset[]> {
    return this.http.get<IAutomaticPointPreset[]>(`${this.apiUrl}/asset/${assetId}`).pipe(
      map(presets => {
        // Always include the default preset at the beginning
        const defaultPreset = this.createDefaultPreset('default', assetId);
        return [defaultPreset, ...presets];
      }),
      tap(presets => this.presetsSubject.next(presets))
    );
  }

  getById(id: string): Observable<IAutomaticPointPreset> {
    return this.http.get<IAutomaticPointPreset>(`${this.apiUrl}/${id}`);
  }

  /** The synthesized built-in Default preset (never exists server-side under a real id). */
  getDefaultPreset(assetId: string): IAutomaticPointPreset {
    return this.createDefaultPreset('default', assetId);
  }

  create(dto: CreateAutomaticPointPresetDto): Observable<IAutomaticPointPreset> {
    const cleanedDto = this.cleanPresetDto(dto);
    return this.http.post<IAutomaticPointPreset>(this.apiUrl, cleanedDto);
  }

  update(dto: UpdateAutomaticPointPresetDto): Observable<IAutomaticPointPreset> {
    const cleanedDto = this.cleanPresetDto(dto);
    return this.http.put<IAutomaticPointPreset>(`${this.apiUrl}/${dto.id}`, cleanedDto);
  }

  private cleanPresetDto<T extends CreateAutomaticPointPresetDto | UpdateAutomaticPointPresetDto>(dto: T): T {
    const cleaned = { ...dto };

    if (cleaned.platesSettings?.PositionPointCriteriaList) {
      cleaned.platesSettings = {
        ...cleaned.platesSettings,
        PositionPointCriteriaList: cleaned.platesSettings.PositionPointCriteriaList.map(item => ({
          ...item,
          Area: item.Area ?? 0,
          NumberOfPoints: item.NumberOfPoints ?? 0,
          NumberOfRows: item.NumberOfRows ?? 0
        }))
      };
    }

    if (cleaned.stiffenersSettings?.PositionPointCriteriaList) {
      cleaned.stiffenersSettings = {
        ...cleaned.stiffenersSettings,
        PositionPointCriteriaList: cleaned.stiffenersSettings.PositionPointCriteriaList.map(item => ({
          ...item,
          Length: item.Length ?? 0,
          NumberOfPoints: item.NumberOfPoints ?? 0
        }))
      };
    }

    if (cleaned.facePlatesSettings?.PositionPointCriteriaList) {
      cleaned.facePlatesSettings = {
        ...cleaned.facePlatesSettings,
        PositionPointCriteriaList: cleaned.facePlatesSettings.PositionPointCriteriaList.map(item => ({
          ...item,
          Length: item.Length ?? 0,
          NumberOfPoints: item.NumberOfPoints ?? 0
        }))
      };
    }

    if (cleaned.bracketSettings?.PositionPointCriteriaList) {
      cleaned.bracketSettings = {
        ...cleaned.bracketSettings,
        PositionPointCriteriaList: cleaned.bracketSettings.PositionPointCriteriaList.map(item => ({
          ...item,
          Area: item.Area ?? 0,
          NumberOfPointsWeb: item.NumberOfPointsWeb ?? 0,
          NumberOfPointsFlange: item.NumberOfPointsFlange ?? 0
        }))
      };
    }

    return cleaned;
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  rename(id: string, newName: string): Observable<IAutomaticPointPreset> {
    return this.update({ id, name: newName });
  }

  // Mock implementations

  private getMockPresetsByAssetId(assetId: string): Observable<IAutomaticPointPreset[]> {
    // Ensure there's always a default preset for the asset
    let assetPresets = this.mockPresets.filter(p => p.assetId === assetId);

    if (assetPresets.length === 0) {
      const defaultPreset = this.createDefaultPreset(uuid(), assetId);
      this.mockPresets.push(defaultPreset);
      assetPresets = [defaultPreset];
    }

    return of(assetPresets).pipe(
      delay(300),
      tap(presets => this.presetsSubject.next(presets))
    );
  }

  private getMockPresetById(id: string): Observable<IAutomaticPointPreset | null> {
    const preset = this.mockPresets.find(p => p.id === id);
    return of(preset || null).pipe(delay(200));
  }

  // Helper methods

  createPresetFromCurrentSettings(
    assetId: string,
    name: string,
    platesSettings: PlatesSettings,
    stiffenersSettings: StiffenersSettings,
    facePlatesSettings: FacePlatesSettings,
    bracketSettings: BracketSettings
  ): Observable<IAutomaticPointPreset> {
    return this.create({
      assetId,
      name,
      platesSettings,
      stiffenersSettings,
      facePlatesSettings,
      bracketSettings
    });
  }

  // Default preset creation

  private createDefaultPreset(id: string, assetId: string): IAutomaticPointPreset {
    return {
      id,
      assetId,
      name: 'Default',
      isDefault: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'System',
      platesSettings: this.getDefaultPlatesSettings(),
      stiffenersSettings: this.getDefaultStiffenersSettings(),
      facePlatesSettings: this.getDefaultFacePlatesSettings(),
      bracketSettings: this.getDefaultBracketSettings()
    };
  }

  private getDefaultPlatesSettings(): PlatesSettings {
    const settings = new PlatesSettings();
    settings.PositionGaugingsforPlates = true;
    settings.PositionGaugingsforBays = false;
    settings.PositionGaugingsforCriticalAreas = false;
    settings.CriticalAreaThreshold = 0;
    settings.PositionGaugingsforAnomalies = false;
    settings.AnomaliesThreshold = 0;
    settings.PointsArrangements = PointsArrangements.Pattern;
    settings.MinimumDistanceBetweenPoints = 0.5;
    settings.MinimumDistanceFromEdges = 0.12;
    settings.MinimumDistanceFromElements = 0.05;
    settings.PositionPointCriteriaList = [
      { Area: 0.09, NumberOfPoints: 1, AddCenterPoint: true, NumberOfRows: 0 },
      { Area: 0.50, NumberOfPoints: 2, AddCenterPoint: true, NumberOfRows: 0 },
      { Area: 1.00, NumberOfPoints: 4, AddCenterPoint: true, NumberOfRows: 0 },
      { Area: null, NumberOfPoints: null, AddCenterPoint: false, NumberOfRows: 0 },
      { Area: null, NumberOfPoints: null, AddCenterPoint: false, NumberOfRows: 0 },
      { Area: null, NumberOfPoints: null, AddCenterPoint: false, NumberOfRows: 0 }
    ];
    return settings;
  }

  private getDefaultStiffenersSettings(): StiffenersSettings {
    const settings = new StiffenersSettings();
    settings.PositionGaugingPointsOnStiffeners = true;
    settings.PositionGaugingPointsOnBays = false;
    settings.InsertPointsOnWeb = true;
    settings.InsertPointsOnFlange = true;
    settings.PositionGaugingsforCriticalAreas = false;
    settings.CriticalAreaThreshold = 0;
    settings.PositionGaugingPointsOnFrames = false;
    settings.FrameSpacing = 0;
    settings.PositionPointCriteriaList = [
      { Length: 0.9, NumberOfPoints: 1 },
      { Length: 4, NumberOfPoints: 2 },
      { Length: 12, NumberOfPoints: 3 },
      { Length: null, NumberOfPoints: null },
      { Length: null, NumberOfPoints: null },
      { Length: null, NumberOfPoints: null }
    ];
    return settings;
  }

  private getDefaultFacePlatesSettings(): FacePlatesSettings {
    const settings = new FacePlatesSettings();
    settings.PositionGaugingPointsOnFacePlates = true;
    settings.PositionGaugingPointsOnBays = false;
    settings.PositionGaugingsforCriticalAreas = false;
    settings.CriticalAreaThreshold = 0;
    settings.PositionPointCriteriaList = [
      { Length: 0.01, NumberOfPoints: 1 },
      { Length: 5, NumberOfPoints: 3 },
      { Length: null, NumberOfPoints: null },
      { Length: null, NumberOfPoints: null },
      { Length: null, NumberOfPoints: null },
      { Length: null, NumberOfPoints: null }
    ];
    return settings;
  }

  private getDefaultBracketSettings(): BracketSettings {
    const settings = new BracketSettings();
    settings.PositionGaugingPointsOnBrackets = true;
    settings.PositionGaugingPointsOnBays = false;
    settings.PositionGaugingsforCriticalAreas = false;
    settings.CriticalAreaThreshold = 0;
    settings.PointsArrangement = PointsArrangements.DescendingDiagonal;
    settings.PositionPointCriteriaList = [
      { Area: 0.5, NumberOfPointsWeb: 1, NumberOfPointsFlange: 1 },
      { Area: 5, NumberOfPointsWeb: 3, NumberOfPointsFlange: 3 },
      { Area: 10, NumberOfPointsWeb: 4, NumberOfPointsFlange: 4 },
      { Area: null, NumberOfPointsWeb: null, NumberOfPointsFlange: null }
    ];
    return settings;
  }
}
